/**
 * CRM Lead Detail — Phase 2: Internal Lead Foundation.
 *
 * Single-lead view. Edits: name, status, assigned staff, follow-up date,
 * form_submitted, internal notes. Surfaces linked website vehicle (read-only
 * sidecar) and reserves space for future WhatsApp conversation history +
 * manual takeover (Phase 3+).
 *
 * Mounts at `/admin/crm/:id`.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { resolvePhotoUrl } from "@/lib/img";
import { ArrowLeft, ChatCircle, ClockClockwise, Lock, Car, Image as ImageIcon, Lightbulb } from "@phosphor-icons/react";
import WaCloudSafetyBanner from "@/components/WaCloudSafetyBanner";
import AutoIntakePanel from "@/components/AutoIntakePanel";
import WhatsAppIntakePanel from "@/components/WhatsAppIntakePanel";
import WhatsAppQaPanel from "@/components/WhatsAppQaPanel";
import PhoneQuickActions from "@/components/PhoneQuickActions";
import EligibilityPanel from "@/components/EligibilityPanel";

export default function CrmLeadDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusOpts, setStatusOpts] = useState([]);
  const [staff, setStaff] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  // Phase 7.2 — draft text is lifted here so the Guided Intake panel can
  // "Insert into Draft". The free-form draft box in the conversation card
  // is now controlled by this state, but admin must still press Send manually.
  const [draftText, setDraftText] = useState("");
  const draftRef = useRef(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/leads/${id}`);
      setLead(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lead not found");
      nav("/admin/crm");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    api.get("/admin/crm/statuses").then(({ data }) => setStatusOpts(data.statuses || [])).catch(() => {});
    api.get("/admin/crm/staff").then(({ data }) => setStaff(data.staff || [])).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const patch = async (changes) => {
    try {
      const { data } = await api.patch(`/admin/crm/leads/${id}`, changes);
      setLead(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const addNote = async () => {
    const t = noteText.trim();
    if (!t) return;
    setSavingNote(true);
    try {
      const { data } = await api.post(`/admin/crm/leads/${id}/notes`, { text: t });
      setLead(data);
      setNoteText("");
      toast.success("Note added");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to add note");
    } finally {
      setSavingNote(false);
    }
  };

  if (loading || !lead) {
    return <div className="p-8 text-center text-white/40 text-sm" data-testid="crm-lead-detail-loading">Loading lead…</div>;
  }

  return (
    <div className="max-w-5xl mx-auto space-y-5" data-testid="crm-lead-detail-page">
      <WaCloudSafetyBanner />
      {/* Header */}
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={() => nav("/admin/crm")} data-testid="crm-back-btn" className="text-white/55 hover:text-white">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] text-white/40 tracking-[0.2em] uppercase font-display">CRM Lead</div>
          <div className="text-white text-lg sm:text-xl font-semibold tracking-tight">
            {lead.name || <span className="text-white/45 italic">Unnamed</span>}
            <span className="text-white/40 text-sm ml-2 font-mono">{lead.phone}</span>
          </div>
          {lead.phone && (
            <div className="mt-2" data-testid="crm-lead-phone-actions">
              <PhoneQuickActions phone={lead.phone} testidPrefix="crm-lead-phoneqa" />
            </div>
          )}
          {/* Phase 10.6 — Needs-Attention banner + Mark Reviewed action */}
          {lead.needs_attention && (
            <div data-testid="crm-lead-needs-attention-banner"
                 className="mt-3 border border-amber-500/60 bg-amber-500/10 px-3 py-2 flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-widest uppercase font-bold text-amber-300 flex items-center gap-1.5">
                  ⚡ Customer Replied — Needs Attention
                  <span className="text-[9px] font-normal text-amber-200/70">· {lead.attention_reason || "review"}</span>
                </div>
                {lead.last_customer_reply_preview && (
                  <div className="text-[11px] text-white/80 italic mt-1 truncate">
                    &ldquo;{lead.last_customer_reply_preview}&rdquo;
                  </div>
                )}
                <div className="text-[9px] text-white/50 mt-1 font-mono">
                  {lead.last_customer_reply_type || "text"} · {lead.customer_replied_after_form_link_at || "—"}
                </div>
              </div>
              <button
                type="button"
                data-testid="crm-lead-mark-reviewed"
                onClick={async () => {
                  try {
                    await api.post(`/admin/crm/leads/${lead.id}/mark-reviewed`);
                    toast.success("Marked as reviewed");
                    await refresh();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || "Failed");
                  }
                }}
                className="shrink-0 text-[10px] tracking-widest uppercase font-bold py-1.5 px-2.5 border border-amber-500 text-amber-300 hover:bg-amber-500/20"
              >
                Mark Reviewed
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Top row: status + meta */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KV label="Status">
          <select
            data-testid="crm-status-select"
            value={lead.status}
            onChange={(e) => patch({ status: e.target.value })}
            className="qd-input-block w-full py-2 text-sm"
          >
            {statusOpts.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </KV>
        <KV label="Assigned Staff">
          <select
            data-testid="crm-assign-select"
            value={lead.assigned_to_user_id || ""}
            onChange={(e) => patch({ assigned_to_user_id: e.target.value })}
            className="qd-input-block w-full py-2 text-sm"
          >
            <option value="">— Unassigned —</option>
            {staff.map((u) => (
              <option key={u.id} value={u.id}>
                {(u.name || u.phone)} · {u.role}
              </option>
            ))}
          </select>
        </KV>
        <KV label="Follow-up Date">
          <input
            data-testid="crm-followup-input"
            type="date"
            value={lead.followup_date || ""}
            onChange={(e) => patch({ followup_date: e.target.value })}
            className="qd-input-block w-full py-2 text-sm"
          />
        </KV>
      </div>

      {/* Form-submitted toggle + name editor */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KV label="Name">
          <NameInline value={lead.name || ""} onSave={(v) => patch({ name: v })} />
        </KV>
        <KV label="Source"><div className="text-white/80 text-sm py-2 capitalize">{lead.source}</div></KV>
        <KV label="Form Submitted">
          <div className="flex gap-2 pt-1">
            <button
              data-testid="crm-form-yes"
              onClick={() => patch({ form_submitted: true })}
              className={`px-3 py-1.5 text-[11px] font-display tracking-widest uppercase border ${
                lead.form_submitted
                  ? "bg-emerald-500/20 border-emerald-500 text-emerald-300"
                  : "border-white/15 text-white/45 hover:text-white"
              }`}
            >Yes</button>
            <button
              data-testid="crm-form-no"
              onClick={() => patch({ form_submitted: false })}
              className={`px-3 py-1.5 text-[11px] font-display tracking-widest uppercase border ${
                !lead.form_submitted
                  ? "bg-white/10 border-white/30 text-white"
                  : "border-white/15 text-white/45 hover:text-white"
              }`}
            >No</button>
          </div>
        </KV>
      </div>

      {/* Linked vehicle (read-only sidecar) */}
      {lead.linked_vehicle && (
        <div className="border border-emerald-500/20 bg-emerald-500/5 p-3" data-testid="crm-linked-vehicle">
          <div className="text-[10px] text-emerald-400 tracking-widest uppercase font-display mb-1">
            Website Form Match (this phone has an existing vehicle submission)
          </div>
          <Link
            to={`/admin/leads?lead=${lead.linked_vehicle.id}`}
            className="text-white font-medium hover:text-[#FF1E1E]"
          >
            {lead.linked_vehicle.year} {lead.linked_vehicle.make} {lead.linked_vehicle.model}
            {lead.linked_vehicle.registration_number && (
              <span className="text-white/45 text-xs ml-2 font-mono">· {lead.linked_vehicle.registration_number}</span>
            )}
          </Link>
          <div className="text-white/55 text-xs mt-1">
            Status: <span className="text-white/85">{lead.linked_vehicle.status || "—"}</span>
            {lead.linked_vehicle.acquisition_stage && (
              <span> · Stage: <span className="text-white/85">{lead.linked_vehicle.acquisition_stage}</span></span>
            )}
          </div>
        </div>
      )}

      {/* Vehicle details from website form (Phase 3 mirror) */}
      {lead.vehicle_details && <VehicleDetailsCard vd={lead.vehicle_details} />}

      {/* Phase 8.1 — Attribution card (always visible, even when missing) */}
      <AttributionCard lead={lead} onRefresh={refresh} />

      {/* Phase 7.1 — Admin-guided WhatsApp Intake */}
      <WhatsAppIntakeCard lead={lead} onRefresh={refresh} />

      {/* Phase 7.2 — Guided WhatsApp Intake Assistant */}
      <GuidedWhatsAppIntakePanel
        lead={lead}
        onRefresh={refresh}
        onInsertDraft={(text) => {
          setDraftText(text);
          // Focus + scroll the draft box into view so admin can review & edit
          // before pressing Send.
          setTimeout(() => {
            const el = draftRef.current;
            if (el) {
              el.focus();
              el.scrollIntoView({ block: "center", behavior: "smooth" });
            }
          }, 60);
        }}
      />

      {/* Phase 10.1 — Automated WhatsApp Intake control panel */}
      <AutoIntakePanel lead={lead} onRefresh={refresh} />

      {/* Linked Vehicle Submissions — Phase 3.5 history of all website form
          submissions from this phone, chronologically. The primary vehicle
          (latest) remains the one rendered in VehicleDetailsCard above. */}
      {Array.isArray(lead.intake_paths) && lead.intake_paths.length > 0 && (
        <LinkedSubmissionsCard
          paths={lead.intake_paths}
          currentVehicleId={lead.vehicle_details?.vehicle_id || lead.linked_vehicle_id}
        />
      )}

      {/* Notes */}
      <div className="border border-white/10 bg-[#0E0E0E]/60 p-3">
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display mb-2">
          Internal Notes · {(lead.notes || []).length}
        </div>
        <div className="space-y-2 max-h-72 overflow-y-auto mb-3" data-testid="crm-notes-list">
          {(lead.notes || []).length === 0 && (
            <div className="text-white/35 text-xs italic">No notes yet.</div>
          )}
          {(lead.notes || []).slice().reverse().map((n) => (
            <div key={n.id} className="border-l-2 border-white/10 pl-3 py-1">
              <div className="text-white/85 text-sm whitespace-pre-wrap">{n.text}</div>
              <div className="text-white/35 text-[10px] font-mono mt-0.5">
                {n.author_name || "—"} · {(n.created_at || "").replace("T", " ").slice(0, 16)}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <textarea
            data-testid="crm-note-input"
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add an internal note…"
            rows={2}
            className="qd-input-block flex-1 text-sm py-2"
          />
          <button
            data-testid="crm-note-save"
            onClick={addNote}
            disabled={savingNote || !noteText.trim()}
            className="self-end px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-[#FF1E1E] hover:bg-[#E61919] disabled:opacity-50"
          >
            {savingNote ? "Saving…" : "Add Note"}
          </button>
        </div>
      </div>

      {/* Phase 10.1E — WhatsApp Intake Progress + Admin Visibility (read-only) */}
      <WhatsAppIntakePanel lead={lead} />

      {/* Phase 10.2E — Rule-based Eligibility Auto-Classifier (deterministic, no AI, no auto-send) */}
      <EligibilityPanel lead={lead} onChange={refresh} />

      {/* Phase 10.2D — Internal AI Quality Monitor (analysis-only, no auto-send) */}
      <WhatsAppQaPanel leadId={lead?.id} />

      {/* Future-phase placeholders */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-3" data-testid="crm-future-placeholders">
        <WhatsAppConversationCard
          lead={lead}
          onRefresh={refresh}
          draftText={draftText}
          setDraftText={setDraftText}
          draftRef={draftRef}
        />
        <div className="border border-dashed border-white/15 bg-white/[0.02] p-4">
          <div className="flex items-center gap-2 mb-1">
            <Lock size={16} className="text-white/40" weight="bold" />
            <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">Manual Takeover</div>
          </div>
          <div className="text-white/45 text-xs leading-relaxed">
            Reserved for Phase 5+. Once AI-assisted detail collection is enabled, this control will let a human admin pause AI replies on this thread. Currently inactive.
          </div>
        </div>
      </div>

      {/* Audit footer */}
      <div className="text-[10px] text-white/30 font-mono pt-2" data-testid="crm-audit-footer">
        Created {(lead.created_at || "").replace("T", " ").slice(0, 16)} by {lead.created_by?.name || lead.created_by?.phone || "—"}
        {" · "}
        Updated {(lead.updated_at || "").replace("T", " ").slice(0, 16)} by {lead.last_updated_by?.name || lead.last_updated_by?.phone || "—"}
        {" · "}
        Lead ID: <span className="text-white/40">{lead.id}</span>
      </div>
    </div>
  );
}

function KV({ label, children }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] text-white/45 tracking-widest uppercase font-display">{label}</div>
      {children}
    </div>
  );
}

function WhatsAppConversationCard({ lead, onRefresh, draftText, setDraftText, draftRef }) {
  // Phase 7.2 — accept lifted draft props so the Guided Intake panel can
  // "Insert into Draft". Falls back to local state if parent didn't lift.
  const [localText, setLocalText] = useState("");
  const text = draftText !== undefined ? draftText : localText;
  const setText = setDraftText || setLocalText;
  const [sending, setSending] = useState(false);
  const [window, setWindow] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("");
  const [sendingTpl, setSendingTpl] = useState(false);
  const conversation = Array.isArray(lead.conversation) ? lead.conversation : [];

  useEffect(() => {
    api.get(`/admin/wa-cloud/leads/${lead.id}/window-state`)
      .then(({ data }) => setWindow(data))
      .catch(() => setWindow(null));
    api.get("/admin/wa-cloud/templates")
      .then(({ data }) => setTemplates(data.templates || []))
      .catch(() => {});
  }, [lead.id, conversation.length]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    try {
      await api.post(`/admin/wa-cloud/leads/${lead.id}/send`, { text: t });
      toast.success("Sent"); setText(""); onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSending(false); }
  };

  const sendTpl = async () => {
    if (!selectedTpl) return;
    setSendingTpl(true);
    try {
      const { data } = await api.post(`/admin/wa-cloud/leads/${lead.id}/send-template`, {
        template_name: selectedTpl,
      });
      toast.success(data.status === "template_dry_run" ? "Template dry-run stored" : "Template sent");
      setSelectedTpl(""); onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Template send failed");
    } finally { setSendingTpl(false); }
  };

  const fmtTs = (s) => (s || "").replace("T", " ").slice(0, 16);
  const windowOpen = window?.free_form_allowed === true;
  const isDryRun = window?.dry_run_mode === true;
  const tplPreview = templates.find((t) => t.name === selectedTpl);

  return (
    <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-wa-conversation">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <ChatCircle size={16} className="text-emerald-400" weight="bold" />
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
          WhatsApp Conversation · {conversation.length}
        </div>
        <div className="ml-auto text-[10px] text-white/35">
          Meta Cloud API · Manual send only · No AI / chatbot
        </div>
      </div>

      {window && (
        <div
          data-testid="crm-wa-window-state"
          className={`text-[11px] px-3 py-1.5 mb-3 border ${
            windowOpen
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"
              : "border-amber-500/30 bg-amber-500/10 text-amber-200"
          }`}
        >
          <span className="font-display tracking-widest uppercase font-bold mr-2">
            {windowOpen ? "Free-form replies allowed" : "24-hour window closed — template required"}
          </span>
          <span className="text-white/55">{window.message}</span>
          {window.hours_since_inbound != null && (
            <span className="text-white/40 font-mono ml-2">· {window.hours_since_inbound}h since last inbound</span>
          )}
          {isDryRun && <span className="text-amber-400 font-display tracking-wider uppercase ml-2">· Dry-run mode</span>}
        </div>
      )}

      <div className="max-h-96 overflow-y-auto space-y-2 mb-3" data-testid="crm-wa-messages">
        {conversation.length === 0 && (
          <div className="text-white/35 text-xs italic">
            No messages yet. Inbound messages from this phone will appear here once Meta Cloud API is connected.
          </div>
        )}
        {conversation.map((m) => {
          const isOut = m.direction === "out";
          const dry = m.dry_run || m.status === "dry_run" || m.status === "template_dry_run";
          const failed = m.status === "failed";
          const isTpl = m.type === "template";
          return (
            <div
              key={m.id || m.wa_message_id || `${m.timestamp}-${m.direction}`}
              data-testid={`crm-wa-msg-${m.id || m.wa_message_id || ""}`}
              className={`flex ${isOut ? "justify-end" : "justify-start"}`}
            >
              <div className={`max-w-[80%] px-3 py-2 border ${
                failed
                  ? "bg-red-500/10 border-red-500/40"
                  : isOut
                    ? (dry ? "bg-amber-500/10 border-amber-500/30" : "bg-emerald-500/10 border-emerald-500/30")
                    : "bg-white/5 border-white/10"
              }`}>
                {isTpl && (
                  <div className="text-[9px] tracking-widest uppercase text-white/45 font-display mb-1">
                    Template · {m.template_label || m.template_name}
                  </div>
                )}
                {m.text ? (
                  <div className="text-white/90 text-sm whitespace-pre-wrap break-words">{m.text}</div>
                ) : (
                  <div className="text-white/55 text-xs italic">
                    [{m.type || "unknown"}]{" "}
                    {m.media_url
                      ? <a href={m.media_url} target="_blank" rel="noreferrer" className="text-emerald-400 underline">view media</a>
                      : (m.media_id ? `media_id=${m.media_id}` : "(no text)")}
                    {m.media_filename && <span className="text-white/40 font-mono ml-1">· {m.media_filename}</span>}
                    {m.media_caption && <div className="text-white/75 mt-0.5">&ldquo;{m.media_caption}&rdquo;</div>}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-[10px] text-white/40 font-mono">
                  <span>{fmtTs(m.timestamp || m.received_at || m.sent_at)}</span>
                  <span>·</span>
                  <span className="uppercase tracking-wider">{isOut ? "Out" : "In"}</span>
                  {dry && <span className="text-amber-400 tracking-wider uppercase">· {m.status === "template_dry_run" ? "template-dry-run" : "dry-run"}</span>}
                  {failed && <span className="text-red-400 tracking-wider uppercase">· failed</span>}
                  {!dry && !failed && m.status && !["received","queued"].includes(m.status) && (
                    <span className={`tracking-wider uppercase ${
                      m.status === "read" ? "text-blue-400" :
                      m.status === "delivered" ? "text-emerald-400" :
                      m.status === "sent" ? "text-white/55" : "text-white/45"
                    }`}>· {m.status}</span>
                  )}
                  {failed && m.failure_reason && <span className="text-red-300">· {m.failure_reason}</span>}
                  {isOut && m.sent_by_name && <span>· by {m.sent_by_name}</span>}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="border-t border-white/8 pt-3 space-y-3">
        {/* Free-form send (window-open path) */}
        <div className="flex gap-2">
          <textarea
            data-testid="crm-wa-send-input"
            ref={draftRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={windowOpen ? "Type a manual WhatsApp message…" : "24-hour window closed — use the template selector below."}
            rows={2}
            disabled={!windowOpen}
            className="qd-input-block flex-1 text-sm py-2 disabled:opacity-40 disabled:cursor-not-allowed"
          />
          <button
            data-testid="crm-wa-send-btn"
            onClick={send}
            disabled={sending || !text.trim() || !windowOpen}
            className="self-end px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {sending ? "Sending…" : "Send"}
          </button>
        </div>

        {/* Template send (always available, especially when window is closed) */}
        <div className="border-t border-white/8 pt-3" data-testid="crm-wa-template-sender">
          <div className="text-[10px] text-white/55 tracking-widest uppercase font-display mb-2">
            Approved Template Message {!windowOpen && <span className="text-amber-400">· required outside 24h window</span>}
          </div>
          <div className="flex flex-wrap gap-2 items-stretch">
            <select
              data-testid="crm-wa-template-select"
              value={selectedTpl}
              onChange={(e) => setSelectedTpl(e.target.value)}
              className="qd-input-block py-2 text-sm min-w-[220px] flex-1"
            >
              <option value="">— Select an approved template —</option>
              {templates.map((t) => (
                <option key={t.name} value={t.name}>{t.label}</option>
              ))}
            </select>
            <button
              data-testid="crm-wa-template-send-btn"
              onClick={sendTpl}
              disabled={sendingTpl || !selectedTpl}
              className="self-stretch px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-amber-600 hover:bg-amber-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {sendingTpl ? "Sending…" : (isDryRun ? "Dry Run Template" : "Send Template")}
            </button>
          </div>
          {tplPreview && (
            <div className="mt-2 p-3 border border-white/10 bg-black/30" data-testid="crm-wa-template-preview">
              <div className="text-[10px] text-white/45 tracking-widest uppercase font-display mb-1">
                Preview · {tplPreview.language} · {tplPreview.status}
              </div>
              <div className="text-white/85 text-sm whitespace-pre-wrap">{tplPreview.body}</div>
            </div>
          )}
        </div>

        <div className="text-[10px] text-white/35">
          Safety: no pricing, no offers, no negotiation. Templates are pre-approved by Meta.
        </div>
      </div>
    </div>
  );
}

function IntakeField({ block, k, label, type = "text", required, options, value, onChange }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-1">
        {label}{required && <span className="text-[#FF1E1E] ml-1">*</span>}
      </div>
      {options ? (
        <select
          data-testid={`intake-${block}-${k}`}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="qd-input-block w-full py-1.5 text-sm"
        >
          <option value="">—</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          data-testid={`intake-${block}-${k}`}
          type={type === "number" ? "text" : type}
          inputMode={type === "number" ? "numeric" : undefined}
          value={value ?? ""}
          onChange={(e) => onChange(type === "number" ? e.target.value.replace(/[^\d]/g, "") : e.target.value)}
          className="qd-input-block w-full py-1.5 text-sm"
        />
      )}
    </label>
  );
}

function WhatsAppIntakeCard({ lead, onRefresh }) {
  const initial = lead.whatsapp_intake || {
    intake_status: "not_started",
    vehicle_details: {}, ownership_details: {}, condition_details: {},
    seller_goal: "", expected_price: "",
    documents_received: {}, missing_required_fields: [], missing_recommended_items: [],
  };
  const [draft, setDraft] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(lead.whatsapp_intake || initial); /* eslint-disable-next-line */ }, [lead.id, lead.whatsapp_intake?.updated_at]);

  const setBlock = (block, key, value) =>
    setDraft((d) => ({ ...d, [block]: { ...(d[block] || {}), [key]: value } }));

  const save = async (patch = {}) => {
    setSaving(true);
    try {
      const body = {
        vehicle_details: draft.vehicle_details, ownership_details: draft.ownership_details,
        condition_details: draft.condition_details, seller_goal: draft.seller_goal || null,
        expected_price: draft.expected_price ? parseInt(draft.expected_price, 10) : null,
        ...patch,
      };
      await api.patch(`/admin/crm/leads/${lead.id}/whatsapp-intake`, body);
      toast.success("Intake saved"); onRefresh?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const markStatus = async (status) => {
    setSaving(true);
    try {
      await api.patch(`/admin/crm/leads/${lead.id}/whatsapp-intake`, { intake_status: status });
      toast.success(`Marked: ${status}`); onRefresh?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Status change failed"));
    } finally { setSaving(false); }
  };

  const missingReq = draft.missing_required_fields || [];
  const missingRec = draft.missing_recommended_items || [];
  const status = draft.intake_status || "not_started";

  const f = (block, k, label, opts = {}) => (
    <IntakeField
      block={block} k={k} label={label}
      type={opts.type} required={opts.required} options={opts.options}
      value={draft[block]?.[k]}
      onChange={(v) => setBlock(block, k, v)}
    />
  );

  return (
    <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-wa-intake">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Car size={16} className="text-emerald-400" weight="bold" />
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
          WhatsApp Vehicle Intake
        </div>
        <span className={`text-[9px] tracking-widest uppercase font-display px-2 py-0.5 border ${
          status === "ready_for_evaluation" || status === "details_collected"
            ? "bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
            : status === "in_progress"
              ? "bg-sky-500/15 border-sky-500/30 text-sky-300"
              : "bg-white/5 border-white/15 text-white/55"
        }`}>{status.replace(/_/g, " ")}</span>
        <div className="ml-auto text-[10px] text-white/35">Admin-guided · No auto-reply</div>
      </div>

      {/* Step 1 — Vehicle */}
      <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-2">Step 1 · Vehicle Details</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4">
        {f("vehicle_details", "brand", "Brand", { required: true })}
        {f("vehicle_details", "model", "Model", { required: true })}
        {f("vehicle_details", "variant", "Variant")}
        {f("vehicle_details", "year", "Year", { type: "number", required: true })}
        {f("vehicle_details", "km_driven", "KM Driven", { type: "number", required: true })}
        {f("vehicle_details", "fuel_type", "Fuel", { required: true, options: ["Petrol","Diesel","CNG","Hybrid","Electric","LPG"] })}
        {f("vehicle_details", "transmission", "Transmission", { required: true, options: ["Manual","Automatic","AMT","CVT","DCT"] })}
        {f("vehicle_details", "registration_region", "Registration Region")}
        {f("vehicle_details", "registration_number", "Registration Number")}
      </div>

      {/* Step 2 — Ownership */}
      <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-2">Step 2 · Ownership</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4">
        {f("ownership_details", "owners", "Number of Owners", { type: "number", required: true })}
        {f("ownership_details", "hypothecation", "Hypothecation Active", { required: true, options: ["Yes","No","Unknown"] })}
        {f("ownership_details", "outstanding_loan", "Outstanding Loan (₹)", { type: "number" })}
        {f("ownership_details", "registration_city_state", "Reg City/State")}
      </div>

      {/* Step 3 — Condition */}
      <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-2">Step 3 · Condition</div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-4">
        {f("condition_details", "condition_score", "Condition Score (1-10)", { type: "number", required: true })}
        {f("condition_details", "accident_history", "Accident History", { required: true, options: ["None","Minor","Major","Not Sure"] })}
        {f("condition_details", "service_history", "Service History", { required: true, options: ["Regular","Partial","None","Not Sure"] })}
      </div>

      {/* Step 4 — Seller Goal */}
      <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-2">Step 4 · Seller Goal (optional)</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-4">
        <label className="block">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-1">Seller Goal</div>
          <select
            data-testid="intake-seller-goal"
            value={draft.seller_goal || ""}
            onChange={(e) => setDraft({ ...draft, seller_goal: e.target.value })}
            className="qd-input-block w-full py-1.5 text-sm"
          >
            <option value="">—</option>
            {["Get an Offer","Sell Quickly","Sell at the Best Possible Price","Upgrade to Another Vehicle","Understand Current Market Value","Vehicle No Longer Needed","Other"].map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-1">Expected Price (₹)</div>
          <input
            data-testid="intake-expected-price"
            value={draft.expected_price || ""}
            onChange={(e) => setDraft({ ...draft, expected_price: e.target.value.replace(/[^\d]/g, "") })}
            className="qd-input-block w-full py-1.5 text-sm"
          />
        </label>
      </div>

      {/* Missing fields */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className={`border p-2.5 ${missingReq.length ? "border-red-500/30 bg-red-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`} data-testid="intake-missing-required">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-1">
            Required Missing · {missingReq.length}
          </div>
          {missingReq.length === 0
            ? <div className="text-emerald-300 text-xs">All required fields complete ✓</div>
            : <ul className="text-red-300 text-xs space-y-0.5">{missingReq.map((f) => <li key={f}>· {f}</li>)}</ul>}
        </div>
        <div className="border border-amber-500/20 bg-amber-500/5 p-2.5" data-testid="intake-missing-recommended">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-1">
            Recommended Missing · {missingRec.length}
          </div>
          {missingRec.length === 0
            ? <div className="text-emerald-300 text-xs">All documents tagged ✓</div>
            : <ul className="text-amber-200 text-xs space-y-0.5">{missingRec.map((f) => <li key={f}>· {f}</li>)}</ul>}
        </div>
      </div>

      {/* Helper prompts — admin-copy only, never auto-sent */}
      <div className="border border-white/10 bg-black/30 p-2.5 mb-4" data-testid="intake-helper-prompts">
        <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-2">Admin Helper Prompts (copy / never auto-sent)</div>
        {[
          ["Vehicle basics", "Please share your vehicle brand, model, year and kilometres driven."],
          ["Ownership", "Please confirm the number of owners and whether the vehicle has active hypothecation."],
          ["Condition", "Please confirm the vehicle condition, accident history and service history."],
          ["Photos", "Please share clear photos of the front, rear, both sides, interior, odometer and RC front/back."],
        ].map(([label, prompt]) => (
          <div key={label} className="flex items-center gap-2 py-1">
            <span className="text-[10px] text-white/45 tracking-wider uppercase w-24">{label}</span>
            <span className="flex-1 text-xs text-white/70 truncate">{prompt}</span>
            <button
              data-testid={`intake-copy-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              onClick={() => { navigator.clipboard.writeText(prompt); toast.success("Copied"); }}
              className="text-[10px] font-display tracking-widest uppercase text-emerald-400 hover:text-emerald-300 whitespace-nowrap"
            >Copy</button>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-2 border-t border-white/8 pt-3">
        <button
          data-testid="intake-save"
          onClick={() => save()}
          disabled={saving}
          className="px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50"
        >{saving ? "Saving…" : "Save Intake Progress"}</button>
        <button
          data-testid="intake-mark-collected"
          onClick={() => markStatus("details_collected")}
          disabled={saving || missingReq.length > 0}
          title={missingReq.length ? `Missing required: ${missingReq.join(", ")}` : ""}
          className="px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-sky-600 hover:bg-sky-500 disabled:opacity-40 disabled:cursor-not-allowed"
        >Mark Details Collected</button>
        <button
          data-testid="intake-mark-ready"
          onClick={() => markStatus("ready_for_evaluation")}
          disabled={saving || missingReq.length > 0}
          title={missingReq.length ? `Missing required: ${missingReq.join(", ")}` : ""}
          className="px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-[#FF1E1E] hover:bg-[#E61919] disabled:opacity-40 disabled:cursor-not-allowed"
        >Mark Ready for Evaluation</button>
      </div>

      {/* Phase 7.3 — Promote to Evaluation */}
      <PromoteToEvaluation lead={lead} onRefresh={onRefresh} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Phase 7.2 — Guided WhatsApp Intake Assistant
// ---------------------------------------------------------------------------
// Operator helper only. Never sends messages. Insert-into-draft places copy
// into the WhatsApp Conversation card's free-form textarea — admin must
// still review, edit if needed, and press Send manually. When the 24-hour
// window is closed, Insert/Send is disabled and the panel directs the admin
// to send an approved template first (existing Phase 6 flow).
function GuidedWhatsAppIntakePanel({ lead, onRefresh, onInsertDraft }) {
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyKey, setBusyKey] = useState(null);
  const [windowOpen, setWindowOpen] = useState(null);  // null = unknown

  const loadGuidance = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/leads/${lead.id}/whatsapp-intake/guidance`);
      setG(data);
    } catch (e) {
      // Silently fail — operator can still use Phase 7.1 form.
    } finally { setLoading(false); }
  };

  useEffect(() => {
    loadGuidance();
    api.get(`/admin/wa-cloud/leads/${lead.id}/window-state`)
      .then(({ data }) => setWindowOpen(data?.free_form_allowed === true))
      .catch(() => setWindowOpen(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.id, lead.whatsapp_intake?.updated_at, lead.conversation?.length]);

  const recordAsked = async (group, insertedIntoDraft) => {
    setBusyKey(group.key + (insertedIntoDraft ? ":insert" : ":mark"));
    try {
      await api.post(`/admin/crm/leads/${lead.id}/whatsapp-intake/ask-history`, {
        group_key:           group.key,
        group_label:         group.label,
        message_text:        group.message,
        inserted_into_draft: !!insertedIntoDraft,
      });
      await loadGuidance();
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not record ask history");
    } finally { setBusyKey(null); }
  };

  const onCopy = async (group) => {
    try {
      await navigator.clipboard.writeText(group.message);
      toast.success(`Copied · ${group.label}`);
      await recordAsked(group, false);
    } catch { toast.error("Copy failed"); }
  };

  const onInsert = (group) => {
    if (windowOpen === false) {
      toast.error("24-hour WhatsApp window closed — send an approved template first.");
      return;
    }
    onInsertDraft?.(group.message);
    toast.success(`Inserted into draft · ${group.label} (review & press Send manually)`);
    recordAsked(group, true);
  };

  const onMark = (group) => recordAsked(group, false);

  if (loading && !g) {
    return (
      <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-wa-guided-panel-loading">
        <div className="text-white/40 text-xs">Loading guided intake…</div>
      </div>
    );
  }
  if (!g) return null;

  const groups        = g.groups || [];
  const nextKey       = g.next_group_key;
  const nextGroup     = groups.find((x) => x.key === nextKey) || null;
  const askHistory    = (g.ask_history || []).slice().reverse();
  const missingReq    = g.missing_required_fields || [];

  const KIND_CLASSES = {
    required:    { badge: "bg-red-500/15 border-red-500/40 text-red-200",         label: "Required" },
    recommended: { badge: "bg-amber-500/15 border-amber-500/40 text-amber-200",   label: "Recommended" },
    optional:    { badge: "bg-white/5  border-white/20 text-white/55",            label: "Optional" },
  };

  return (
    <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-wa-guided-panel">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <Lightbulb size={16} className="text-amber-300" weight="bold" />
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
          Guided WhatsApp Intake
        </div>
        <span className="text-[9px] tracking-widest uppercase font-display px-2 py-0.5 border bg-white/5 border-white/15 text-white/55">
          {(g.intake_status || "not_started").replace(/_/g, " ")}
        </span>
        <div className="ml-auto text-[10px] text-white/35">
          Operator helper · No auto-send · No AI
        </div>
      </div>

      {/* Window-closed notice */}
      {windowOpen === false && (
        <div className="border border-amber-500/30 bg-amber-500/10 text-amber-200 px-3 py-2 mb-3 text-[11px]"
             data-testid="crm-wa-guided-window-closed">
          <div className="font-display tracking-widest uppercase font-bold mb-0.5">
            24-hour WhatsApp window closed
          </div>
          <div className="text-white/80">
            Send an approved template first (see template selector below). After the customer
            replies, continue the guided intake here.
          </div>
        </div>
      )}

      {/* Next recommended question */}
      <div className="border border-emerald-500/25 bg-emerald-500/5 p-3 mb-4"
           data-testid="crm-wa-guided-next">
        <div className="text-[10px] tracking-widest uppercase font-display text-emerald-300 mb-1">
          Next Recommended Question
        </div>
        {nextGroup ? (
          <>
            <div className="text-white/85 text-sm font-semibold mb-1">
              {nextGroup.label}
              <span className="ml-2 text-[10px] font-display tracking-widest uppercase text-white/45">
                · {KIND_CLASSES[nextGroup.kind].label}
              </span>
            </div>
            <div className="text-white/70 text-xs whitespace-pre-wrap font-mono leading-relaxed">
              {nextGroup.message}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              <button
                data-testid="crm-wa-guided-next-insert"
                onClick={() => onInsert(nextGroup)}
                disabled={windowOpen === false || busyKey?.startsWith(nextGroup.key)}
                className="px-3 py-1.5 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >Insert into Draft</button>
              <button
                data-testid="crm-wa-guided-next-copy"
                onClick={() => onCopy(nextGroup)}
                className="px-3 py-1.5 text-[11px] font-display tracking-widest uppercase text-white/85 border border-white/15 hover:bg-white/5"
              >Copy</button>
              <button
                data-testid="crm-wa-guided-next-mark"
                onClick={() => onMark(nextGroup)}
                className="px-3 py-1.5 text-[11px] font-display tracking-widest uppercase text-white/85 border border-white/15 hover:bg-white/5"
              >Mark as Asked</button>
            </div>
          </>
        ) : (
          <div className="text-emerald-300 text-xs">
            All groups complete. No further questions recommended.
          </div>
        )}
      </div>

      {/* Required missing alert */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div className={`border p-2.5 ${missingReq.length ? "border-red-500/30 bg-red-500/5" : "border-emerald-500/30 bg-emerald-500/5"}`}
             data-testid="crm-wa-guided-missing-required">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-1">
            Required Missing · {missingReq.length}
          </div>
          {missingReq.length === 0
            ? <div className="text-emerald-300 text-xs">All required fields complete ✓</div>
            : <ul className="text-red-300 text-xs space-y-0.5">{missingReq.map((f) => <li key={f}>· {f}</li>)}</ul>}
        </div>
        <div className="border border-amber-500/20 bg-amber-500/5 p-2.5"
             data-testid="crm-wa-guided-missing-recommended">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-1">
            Recommended Missing · {(g.missing_recommended_items || []).length}
          </div>
          {(g.missing_recommended_items || []).length === 0
            ? <div className="text-emerald-300 text-xs">All documents tagged ✓</div>
            : <ul className="text-amber-200 text-xs space-y-0.5">{g.missing_recommended_items.map((f) => <li key={f}>· {f}</li>)}</ul>}
        </div>
      </div>

      {/* All question groups */}
      <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-2">
        Question Groups
      </div>
      <div className="space-y-2 mb-4" data-testid="crm-wa-guided-groups">
        {groups.map((grp) => {
          const cls   = KIND_CLASSES[grp.kind] || KIND_CLASSES.optional;
          const asked = !!grp.last_asked_at;
          const insertBusy = busyKey === grp.key + ":insert";
          const markBusy   = busyKey === grp.key + ":mark";
          return (
            <details
              key={grp.key}
              data-testid={`crm-wa-guided-group-${grp.key}`}
              className="border border-white/10 bg-black/30"
              open={grp.key === nextKey && !grp.is_complete}
            >
              <summary className="cursor-pointer px-3 py-2 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-white/90 font-medium">{grp.label}</span>
                <span className={`text-[9px] tracking-widest uppercase font-display px-1.5 py-0.5 border ${cls.badge}`}>
                  {cls.label}
                </span>
                {grp.is_complete ? (
                  <span className="text-[9px] tracking-widest uppercase font-display px-1.5 py-0.5 border bg-emerald-500/15 border-emerald-500/30 text-emerald-300"
                        data-testid={`crm-wa-guided-group-${grp.key}-complete`}>
                    Complete
                  </span>
                ) : (
                  <span className="text-[9px] tracking-widest uppercase font-display text-white/45">
                    · {grp.missing.length} missing
                  </span>
                )}
                {asked && (
                  <span className="text-[9px] tracking-widest uppercase font-display px-1.5 py-0.5 border bg-sky-500/15 border-sky-500/30 text-sky-300"
                        data-testid={`crm-wa-guided-group-${grp.key}-asked`}>
                    Asked
                  </span>
                )}
                <span className="ml-auto text-[10px] text-white/35 font-mono">
                  {grp.last_asked_at ? (grp.last_asked_at).replace("T", " ").slice(0, 16) : ""}
                </span>
              </summary>
              <div className="px-3 pb-3 pt-1">
                {!grp.is_complete && (
                  <div className="text-[10px] text-white/45 mb-1.5">
                    Missing: <span className="text-white/70 font-mono">{grp.missing.join(", ")}</span>
                  </div>
                )}
                <div className="text-white/75 text-xs whitespace-pre-wrap font-mono leading-relaxed border border-white/10 bg-black/40 p-2">
                  {grp.message}
                </div>
                <div className="flex flex-wrap gap-2 mt-2">
                  <button
                    data-testid={`crm-wa-guided-group-${grp.key}-insert`}
                    onClick={() => onInsert(grp)}
                    disabled={windowOpen === false || insertBusy}
                    title={windowOpen === false ? "24h window closed — send a template first" : ""}
                    className="px-3 py-1.5 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed"
                  >{insertBusy ? "…" : "Insert into Draft"}</button>
                  <button
                    data-testid={`crm-wa-guided-group-${grp.key}-copy`}
                    onClick={() => onCopy(grp)}
                    className="px-3 py-1.5 text-[11px] font-display tracking-widest uppercase text-white/85 border border-white/15 hover:bg-white/5"
                  >Copy</button>
                  <button
                    data-testid={`crm-wa-guided-group-${grp.key}-mark`}
                    onClick={() => onMark(grp)}
                    disabled={markBusy}
                    className="px-3 py-1.5 text-[11px] font-display tracking-widest uppercase text-white/85 border border-white/15 hover:bg-white/5 disabled:opacity-40"
                  >{markBusy ? "…" : "Mark as Asked"}</button>
                  {asked && (
                    <span className="text-[10px] text-white/40 self-center">
                      Last asked by {grp.last_asked_by || "—"} · {grp.last_asked_method || "—"}
                    </span>
                  )}
                </div>
              </div>
            </details>
          );
        })}
      </div>

      {/* Ask history */}
      <div className="border-t border-white/8 pt-3">
        <div className="text-[10px] tracking-widest uppercase font-display text-white/55 mb-2">
          Ask History · {askHistory.length}
        </div>
        {askHistory.length === 0 ? (
          <div className="text-white/35 text-xs italic">No questions asked yet.</div>
        ) : (
          <ul className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="crm-wa-guided-ask-history">
            {askHistory.map((e) => (
              <li key={e.id} className="text-[11px] border-l-2 border-white/10 pl-2 py-0.5"
                  data-testid={`crm-wa-guided-ask-${e.id}`}>
                <div className="text-white/85">
                  <span className="text-white/55 font-display tracking-wider uppercase mr-1">
                    {e.group_label}
                  </span>
                  {e.inserted_into_draft && (
                    <span className="text-emerald-300 text-[9px] tracking-wider uppercase font-display ml-1">
                      · inserted
                    </span>
                  )}
                </div>
                <div className="text-white/35 text-[10px] font-mono">
                  {(e.asked_at || "").replace("T", " ").slice(0, 16)} · by {e.asked_by_name || "—"}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="text-[10px] text-white/30 mt-3">
        Reminder: customer replies must still be entered manually into the WhatsApp Vehicle
        Intake fields. No auto-parsing, no AI extraction.
      </div>
    </div>
  );
}



function LinkedSubmissionsCard({ paths, currentVehicleId }) {  // Sort newest first.
  const sorted = [...paths].sort((a, b) => (b.at || "").localeCompare(a.at || ""));
  const SOURCE_LABEL = {
    website_form: "Website Form",
    whatsapp:     "WhatsApp",
    manual:       "Manual",
  };
  return (
    <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-linked-submissions">
      <div className="flex items-center justify-between gap-2 mb-3">
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
          Linked Vehicle Submissions · {paths.length}
        </div>
        <div className="text-[10px] text-white/35">Newest first</div>
      </div>
      <div className="space-y-2">
        {sorted.map((p, i) => {
          const isLatest = p.vehicle_id && p.vehicle_id === currentVehicleId;
          return (
            <div
              key={`${p.vehicle_id || "noid"}-${i}`}
              data-testid={`crm-submission-${i}`}
              className={`flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 border ${
                isLatest
                  ? "border-emerald-500/30 bg-emerald-500/5"
                  : "border-white/8 bg-black/30"
              }`}
            >
              <div className="text-white/85 text-xs font-mono whitespace-nowrap">
                {(p.at || "").replace("T", " ").slice(0, 16)}
              </div>
              <div className="text-white text-sm font-medium font-mono">
                {(p.registration_number || "—").toUpperCase()}
              </div>
              <div className="text-white/45 text-[10px] font-mono">
                {(p.vehicle_id || "").slice(0, 8) || "—"}
              </div>
              <div className="text-white/55 text-[10px] tracking-widest uppercase font-display">
                {SOURCE_LABEL[p.source] || p.source}
              </div>
              {isLatest && (
                <span className="text-[9px] font-display tracking-widest uppercase text-emerald-300 bg-emerald-500/15 border border-emerald-500/30 px-1.5 py-0.5">
                  Primary
                </span>
              )}
              {p.vehicle_id && (
                <Link
                  to={`/admin/leads?lead=${p.vehicle_id}`}
                  data-testid={`crm-submission-link-${i}`}
                  className="ml-auto text-[10px] font-display tracking-widest uppercase font-bold text-[#FF1E1E] hover:text-[#FF4040]"
                >
                  Open →
                </Link>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function VehicleDetailsCard({ vd }) {
  const rows = [
    ["Registration", vd.registration_number],
    ["Make", vd.make],
    ["Model", vd.model],
    ["Variant", vd.variant],
    ["Year", vd.year],
    ["Fuel", vd.fuel_type],
    ["Transmission", vd.transmission],
    ["KM Driven", vd.km_driven ? `${Number(vd.km_driven).toLocaleString()} km` : null],
    ["Owners", vd.owners],
    ["Registration State", vd.registration_state],
    ["Hypothecation", vd.hypothecation === true ? "Yes" : (vd.hypothecation === false ? "No" : null)],
    ["Accident History", vd.accident_history],
    ["Service History", vd.service_history],
    ["Expected Price", vd.expected_price ? `₹${Number(vd.expected_price).toLocaleString("en-IN")}` : null],
    ["Preferred Inspection", vd.preferred_inspection_time],
    ["Customer Intent", vd.customer_intent],
  ].filter(([, v]) => v !== null && v !== undefined && v !== "");

  const photos = Array.isArray(vd.photos) ? vd.photos : [];

  return (
    <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-vehicle-details">
      <div className="flex items-center gap-2 mb-3">
        <Car size={16} className="text-[#FF1E1E]" weight="bold" />
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
          Vehicle Details (from website form)
        </div>
        <div className="ml-auto text-[10px] text-white/35 font-mono">
          Submitted: {(vd.submitted_at || "").replace("T", " ").slice(0, 16) || "—"}
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-2.5 mb-4">
        {rows.map(([k, v]) => (
          <div key={k}>
            <div className="text-[9px] text-white/40 tracking-widest uppercase font-display">{k}</div>
            <div className="text-white/85 text-sm break-words">{String(v)}</div>
          </div>
        ))}
        {vd.customer_intent_note && (
          <div className="col-span-2 sm:col-span-3 lg:col-span-4">
            <div className="text-[9px] text-white/40 tracking-widest uppercase font-display">Intent Note</div>
            <div className="text-white/75 text-sm italic">&ldquo;{vd.customer_intent_note}&rdquo;</div>
          </div>
        )}
      </div>

      {photos.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-2 mt-3 pt-3 border-t border-white/8">
            <ImageIcon size={14} className="text-white/55" weight="bold" />
            <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
              Uploaded Photos / RC · {photos.length}
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="crm-photos-grid">
            {photos.map((p, i) => {
              const url = resolvePhotoUrl(p);
              return (
                <a
                  key={`${url}-${i}`}
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`crm-photo-${i}`}
                  className="block aspect-square overflow-hidden bg-black border border-white/10 hover:border-[#FF1E1E] transition-colors"
                >
                  <img src={url} alt={`Vehicle ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </a>
              );
            })}
          </div>
        </>
      )}

      {vd.vehicle_id && (
        <div className="mt-4 pt-3 border-t border-white/8">
          <Link
            to={`/admin/leads?lead=${vd.vehicle_id}`}
            data-testid="crm-open-seller-lead"
            className="inline-flex items-center gap-2 text-[11px] font-display tracking-widest uppercase font-bold text-[#FF1E1E] hover:text-[#FF4040]"
          >
            Open in Seller Leads →
          </Link>
        </div>
      )}
    </div>
  );
}

function NameInline({ value, onSave }) {
  const [v, setV] = useState(value);
  useEffect(() => { setV(value); }, [value]);
  const commit = () => {
    if (v !== value) onSave(v);
  };
  return (
    <input
      data-testid="crm-name-input"
      value={v}
      onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
      placeholder="Customer name"
      className="qd-input-block w-full py-2 text-sm"
    />
  );
}


// ---------------------------------------------------------------------------
// Phase 8.1 — Attribution Card with admin edit + history.
// ---------------------------------------------------------------------------
const ATTR_SOURCES = ["meta", "google", "whatsapp", "organic", "direct", "manual", "unknown"];
const ATTR_SOURCE_TONE_DETAIL = {
  meta:     "bg-blue-500/15 border-blue-500/40 text-blue-200",
  google:   "bg-green-500/15 border-green-500/40 text-green-200",
  whatsapp: "bg-emerald-500/15 border-emerald-500/40 text-emerald-200",
  organic:  "bg-amber-500/10 border-amber-500/30 text-amber-200",
  direct:   "bg-white/5  border-white/15 text-white/60",
  manual:   "bg-violet-500/15 border-violet-500/30 text-violet-200",
  unknown:  "bg-orange-500/10 border-orange-500/30 text-orange-200",
};

function AttributionCard({ lead, onRefresh }) {
  const attr  = lead.attribution || lead.latest_attribution || null;
  const first = lead.first_attribution || null;
  const hist  = Array.isArray(lead.attribution_history) ? lead.attribution_history : [];
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const startEdit = () => {
    setDraft({
      source: attr?.source || "",
      medium: attr?.medium || "",
      campaign_name: attr?.campaign_name || "",
      campaign_id: attr?.campaign_id || "",
      adset_id: attr?.adset_id || "",
      ad_id: attr?.ad_id || "",
      meta_campaign_id: attr?.meta_campaign_id || "",
      meta_adset_id: attr?.meta_adset_id || "",
      meta_ad_id: attr?.meta_ad_id || "",
      platform: attr?.platform || "",
      placement: attr?.placement || "",
      lead_source_detail: attr?.lead_source_detail || "",
      landing_page: attr?.landing_page || "",
      referrer: attr?.referrer || "",
      // Phase 8.2 — lead-level labels (manual only, no AI)
      quality_note: lead.attribution_quality_note || "",
      internal_label: lead.attribution_internal_label || "",
    });
    setNote("");
    setEdit(true);
  };

  const save = async () => {
    setBusy(true);
    try {
      // Only send fields that have been touched (non-empty) or explicitly cleared.
      const payload = { ...draft, note: note.trim() || undefined };
      await api.patch(`/admin/crm/leads/${lead.id}/attribution`, payload);
      toast.success("Attribution updated");
      setEdit(false);
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  if (!attr && !edit) {
    return (
      <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-attribution-card">
        <div className="flex items-center gap-2 mb-2">
          <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">Attribution</div>
          <button
            data-testid="crm-attribution-edit-btn"
            onClick={startEdit}
            className="ml-auto text-[10px] font-display tracking-widest uppercase font-bold text-[#FF1E1E] hover:text-[#FF4040]"
          >Edit Manually</button>
        </div>
        <div className="text-white/45 text-sm italic">No attribution captured.</div>
      </div>
    );
  }

  const tone = ATTR_SOURCE_TONE_DETAIL[attr?.source] || ATTR_SOURCE_TONE_DETAIL.unknown;

  return (
    <div className="border border-white/10 bg-[#0E0E0E]/60 p-4" data-testid="crm-attribution-card">
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">Attribution</div>
        {attr?.source && (
          <span className={`text-[9px] tracking-widest uppercase font-display px-2 py-0.5 border ${tone}`}
                data-testid="crm-attribution-source-badge">
            {attr.source}
          </span>
        )}
        {!edit ? (
          <button
            data-testid="crm-attribution-edit-btn"
            onClick={startEdit}
            className="ml-auto text-[10px] font-display tracking-widest uppercase font-bold text-[#FF1E1E] hover:text-[#FF4040]"
          >Edit Manually</button>
        ) : (
          <div className="ml-auto flex gap-2">
            <button
              onClick={() => setEdit(false)}
              className="text-[10px] font-display tracking-widest uppercase text-white/55 hover:text-white"
            >Cancel</button>
            <button
              data-testid="crm-attribution-save-btn"
              onClick={save}
              disabled={busy}
              className="text-[10px] font-display tracking-widest uppercase font-bold text-white bg-[#FF1E1E] hover:bg-[#E61919] px-2 py-1 disabled:opacity-50"
            >{busy ? "Saving…" : "Save"}</button>
          </div>
        )}
      </div>

      {edit ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-3">
          <AttrField label="Source" value={draft.source}
            onChange={(v) => setDraft({ ...draft, source: v })}
            options={["", ...ATTR_SOURCES]} testid="attr-edit-source" />
          <AttrField label="Medium" value={draft.medium}
            onChange={(v) => setDraft({ ...draft, medium: v })} testid="attr-edit-medium" />
          <AttrField label="Campaign Name" value={draft.campaign_name}
            onChange={(v) => setDraft({ ...draft, campaign_name: v })} testid="attr-edit-campaign-name" />
          <AttrField label="Campaign ID" value={draft.campaign_id}
            onChange={(v) => setDraft({ ...draft, campaign_id: v })} testid="attr-edit-campaign-id" />
          <AttrField label="Ad Set ID" value={draft.adset_id}
            onChange={(v) => setDraft({ ...draft, adset_id: v })} testid="attr-edit-adset-id" />
          <AttrField label="Ad ID" value={draft.ad_id}
            onChange={(v) => setDraft({ ...draft, ad_id: v })} testid="attr-edit-ad-id" />
          <AttrField label="Meta Campaign ID" value={draft.meta_campaign_id}
            onChange={(v) => setDraft({ ...draft, meta_campaign_id: v })} testid="attr-edit-meta-campaign" />
          <AttrField label="Meta Adset ID" value={draft.meta_adset_id}
            onChange={(v) => setDraft({ ...draft, meta_adset_id: v })} testid="attr-edit-meta-adset" />
          <AttrField label="Meta Ad ID" value={draft.meta_ad_id}
            onChange={(v) => setDraft({ ...draft, meta_ad_id: v })} testid="attr-edit-meta-ad" />
          <AttrField label="Platform" value={draft.platform}
            onChange={(v) => setDraft({ ...draft, platform: v })} testid="attr-edit-platform" />
          <AttrField label="Placement" value={draft.placement}
            onChange={(v) => setDraft({ ...draft, placement: v })} testid="attr-edit-placement" />
          <AttrField label="Lead Source Detail" value={draft.lead_source_detail}
            onChange={(v) => setDraft({ ...draft, lead_source_detail: v })} testid="attr-edit-lead-source-detail" />
          <AttrField label="Landing Page" value={draft.landing_page}
            onChange={(v) => setDraft({ ...draft, landing_page: v })} testid="attr-edit-landing" />
          <AttrField label="Referrer" value={draft.referrer}
            onChange={(v) => setDraft({ ...draft, referrer: v })} testid="attr-edit-referrer" />
          {/* Phase 8.2 — Manual quality + internal labels (no AI) */}
          <AttrField label="Quality Note (manual)" value={draft.quality_note}
            onChange={(v) => setDraft({ ...draft, quality_note: v })} testid="attr-edit-quality-note" />
          <AttrField label="Internal Label" value={draft.internal_label}
            onChange={(v) => setDraft({ ...draft, internal_label: v })} testid="attr-edit-internal-label" />
          <label className="block sm:col-span-3">
            <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-1">Audit note</div>
            <input
              data-testid="attr-edit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Why are you editing? (saved to audit log)"
              className="qd-input-block w-full py-1.5 text-sm"
            />
          </label>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-3 text-xs">
          <AttrCell label="Medium" value={attr?.medium} />
          <AttrCell label="Campaign" value={attr?.campaign_name} />
          <AttrCell label="Campaign ID" value={attr?.campaign_id} />
          <AttrCell label="Ad Set ID" value={attr?.adset_id} />
          <AttrCell label="Ad ID" value={attr?.ad_id} />
          <AttrCell label="Meta Ad ID" value={attr?.meta_ad_id} />
          <AttrCell label="Platform" value={attr?.platform} />
          <AttrCell label="Placement" value={attr?.placement} />
          <AttrCell label="Lead Source Detail" value={attr?.lead_source_detail} />
          <AttrCell label="Landing Page" value={attr?.landing_page} mono />
          <AttrCell label="Referrer" value={attr?.referrer} mono />
          <AttrCell label="Latest Captured" value={attr?.captured_at?.replace("T", " ").slice(0, 16)} mono />
          {first && (
            <AttrCell label="First Captured" value={first.captured_at?.replace("T", " ").slice(0, 16)} mono />
          )}
          <AttrCell label="History Entries" value={hist.length} />
          {/* Phase 8.2 — manual labels */}
          {(lead.attribution_quality_note || lead.attribution_internal_label) && (
            <>
              <AttrCell label="Quality Note" value={lead.attribution_quality_note} />
              <AttrCell label="Internal Label" value={lead.attribution_internal_label} />
            </>
          )}
        </div>
      )}

      {hist.length > 0 && !edit && (
        <div className="border-t border-white/8 pt-3">
          <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-2">
            Attribution History · {hist.length}
          </div>
          <ul className="space-y-1 max-h-40 overflow-y-auto" data-testid="crm-attribution-history">
            {hist.slice().reverse().map((h, idx) => (
              <li key={idx} className="text-[11px] border-l-2 border-white/10 pl-2 py-0.5">
                <div className="flex flex-wrap items-center gap-x-2 text-white/75">
                  <span className="text-white/85 font-display tracking-wider uppercase text-[10px]">
                    {h.source || "—"}
                  </span>
                  {h.campaign_name && <span>· {h.campaign_name}</span>}
                  {h.event && <span className="text-white/45 text-[10px] italic">· {h.event}</span>}
                  {h.manual && <span className="text-violet-300 text-[9px] uppercase font-display tracking-wider">· manual</span>}
                </div>
                <div className="text-white/35 text-[10px] font-mono">
                  {(h.captured_at || "").replace("T", " ").slice(0, 16)}
                  {h.actor_name && ` · by ${h.actor_name}`}
                  {Array.isArray(h.changed_fields) && h.changed_fields.length > 0 &&
                    ` · ${h.changed_fields.length} fields changed`}
                </div>
                {h.note && <div className="text-white/55 text-[10px] italic">&ldquo;{h.note}&rdquo;</div>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function AttrCell({ label, value, mono = false }) {
  return (
    <div>
      <div className="text-[9px] tracking-widest uppercase font-display text-white/40">{label}</div>
      <div className={`${mono ? "font-mono break-all" : ""} text-white/85`}>
        {(value === null || value === undefined || value === "")
          ? <span className="text-white/30">—</span>
          : value}
      </div>
    </div>
  );
}

function AttrField({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-1">{label}</div>
      {options ? (
        <select
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="qd-input-block w-full py-1.5 text-sm"
        >
          {options.map((o) => <option key={o} value={o}>{o || "—"}</option>)}
        </select>
      ) : (
        <input
          data-testid={testid}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          className="qd-input-block w-full py-1.5 text-sm"
        />
      )}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Phase 7.3 — Promote WhatsApp intake to Seller/Evaluation workflow
// ---------------------------------------------------------------------------
function PromoteToEvaluation({ lead, onRefresh }) {
  const [preview, setPreview] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const promoted = lead.whatsapp_intake_promotion?.promoted === true;
  const promotedVehicleId = lead.whatsapp_intake_promotion?.promoted_vehicle_id;

  const loadPreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await api.get(`/admin/crm/leads/${lead.id}/whatsapp-intake/promotion-preview`);
      setPreview(data);
      setOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load promotion preview");
    } finally { setLoadingPreview(false); }
  };

  const promote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/crm/leads/${lead.id}/whatsapp-intake/promote`);
      if (data.already_promoted) {
        toast.info(`Already promoted · Vehicle ${data.promoted_vehicle_id?.slice(0, 8)}…`);
      } else {
        toast.success(`Promoted · Seller record ${data.promoted_vehicle_id?.slice(0, 8)}…`);
      }
      setOpen(false);
      onRefresh?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (typeof detail === "object" && detail?.eligibility) {
        toast.error(`Not eligible: ${detail.eligibility.blockers?.join(", ")}`);
      } else {
        toast.error(detail || "Promotion failed");
      }
    } finally { setBusy(false); }
  };

  if (promoted) {
    return (
      <div className="mt-3 pt-3 border-t border-emerald-500/20 bg-emerald-500/5 -mx-4 -mb-4 px-4 py-3"
           data-testid="crm-promotion-already">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[9px] tracking-widest uppercase font-display px-2 py-0.5 border bg-emerald-500/15 border-emerald-500/40 text-emerald-200">
            Already Promoted
          </span>
          <span className="text-white/70 text-xs">
            Seller record: <span className="font-mono text-emerald-300">{promotedVehicleId?.slice(0, 12)}…</span>
          </span>
          <Link
            to={`/admin/seller-leads?focus=${promotedVehicleId}`}
            data-testid="crm-promotion-open-record"
            className="ml-auto text-[10px] font-display tracking-widest uppercase font-bold text-emerald-300 hover:text-emerald-100"
          >Open Seller Record →</Link>
        </div>
        <div className="text-white/45 text-[10px] mt-1 font-mono">
          Promoted {(lead.whatsapp_intake_promotion?.promoted_at || "").replace("T", " ").slice(0, 16)}
          {lead.whatsapp_intake_promotion?.promoted_by_name && ` · by ${lead.whatsapp_intake_promotion.promoted_by_name}`}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="mt-3 pt-3 border-t border-white/8">
        <button
          data-testid="crm-promote-btn"
          onClick={loadPreview}
          disabled={loadingPreview}
          className="px-3 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-violet-600 hover:bg-violet-500 disabled:opacity-50"
        >
          {loadingPreview ? "Loading preview…" : "Promote to Evaluation"}
        </button>
        <span className="ml-3 text-[10px] text-white/45">
          Creates a Seller / Evaluation record from this intake. Admin-confirmed only — no auto-pricing.
        </span>
      </div>

      {open && preview && (
        <div
          className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="crm-promote-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="w-full max-w-3xl bg-[#0E0E0E] border border-white/15 max-h-[90vh] overflow-y-auto">
            <div className="px-4 py-3 border-b border-white/10 flex items-center gap-3">
              <div className="text-[10px] text-white/55 tracking-widest uppercase font-display">
                Promote WhatsApp Intake → Evaluation
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="crm-promote-modal-close"
                className="ml-auto text-white/55 hover:text-white text-sm"
              >Cancel</button>
            </div>

            <div className="p-4 space-y-3 text-sm">
              <PreviewRow label="Customer" value={preview.customer_name} />
              <PreviewRow label="Phone" value={preview.phone} mono />
              {preview.promotion_payload_preview && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-xs"
                     data-testid="crm-promote-payload">
                  <PreviewCell label="Make"        value={preview.promotion_payload_preview.make} />
                  <PreviewCell label="Model"       value={preview.promotion_payload_preview.model} />
                  <PreviewCell label="Variant"     value={preview.promotion_payload_preview.variant} />
                  <PreviewCell label="Year"        value={preview.promotion_payload_preview.year} />
                  <PreviewCell label="Fuel"        value={preview.promotion_payload_preview.fuel_type} />
                  <PreviewCell label="Transmission" value={preview.promotion_payload_preview.transmission} />
                  <PreviewCell label="KM Driven"   value={preview.promotion_payload_preview.km_driven} />
                  <PreviewCell label="Owners"      value={preview.promotion_payload_preview.owners} />
                  <PreviewCell label="Hypothecation" value={preview.promotion_payload_preview.hypothecation ? "Yes" : "No"} />
                  <PreviewCell label="Outstanding Loan" value={preview.promotion_payload_preview.outstanding_loan_amount} />
                  <PreviewCell label="Reg Number"  value={preview.promotion_payload_preview.registration_number} mono />
                  <PreviewCell label="Reg Region"  value={preview.promotion_payload_preview.registration_region} />
                  <PreviewCell label="Condition (0-100)" value={preview.promotion_payload_preview.condition_score} />
                  <PreviewCell label="Accident"    value={preview.promotion_payload_preview.accident_history} />
                  <PreviewCell label="Service"     value={preview.promotion_payload_preview.service_history} />
                  <PreviewCell label="Seller Goal" value={preview.promotion_payload_preview.customer_intent} />
                  <PreviewCell label="Expected Price" value={preview.promotion_payload_preview.expected_price} />
                  <PreviewCell label="Photos Tagged" value={(preview.promotion_payload_preview.photos || []).length} />
                </div>
              )}

              <div className="border border-white/10 p-2">
                <div className="text-[10px] tracking-widest uppercase font-display text-white/45 mb-1">
                  Tagged Documents · {(preview.promotion_payload_preview?.tagged_documents || []).length}
                </div>
                {(preview.promotion_payload_preview?.tagged_documents || []).length === 0 ? (
                  <div className="text-white/30 text-xs italic">No documents tagged yet (not blocking).</div>
                ) : (
                  <ul className="text-[11px] space-y-0.5">
                    {preview.promotion_payload_preview.tagged_documents.map((t, i) => (
                      <li key={i} className="text-white/75">
                        {t.slot}: {t.media_url
                          ? <span className="text-emerald-300">URL attached</span>
                          : <span className="text-amber-300">placeholder ({t.media_id || "no media id"})</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {(preview.eligibility?.missing_recommended_items || []).length > 0 && (
                <div className="border border-amber-500/30 bg-amber-500/5 p-2 text-[11px] text-amber-200"
                     data-testid="crm-promote-missing-recommended">
                  Missing recommended:&nbsp;
                  <span className="font-mono">
                    {preview.eligibility.missing_recommended_items.join(", ")}
                  </span>
                </div>
              )}

              {preview.promotion_payload_preview?.attribution?.source && (
                <div className="border border-white/10 p-2 text-[11px]" data-testid="crm-promote-attribution">
                  <div className="text-white/45 tracking-widest uppercase font-display text-[9px]">Attribution</div>
                  <div className="text-white/85">
                    {preview.promotion_payload_preview.attribution.source}
                    {preview.promotion_payload_preview.attribution.campaign_name &&
                      ` · ${preview.promotion_payload_preview.attribution.campaign_name}`}
                  </div>
                </div>
              )}

              <div className="border border-red-500/30 bg-red-500/5 p-2 text-[11px] text-red-200 leading-relaxed"
                   data-testid="crm-promote-warning">
                {(preview.warnings || []).map((w, i) => <div key={i}>· {w}</div>)}
              </div>

              <div className="flex flex-wrap gap-2 pt-2 border-t border-white/8">
                <button
                  data-testid="crm-promote-confirm"
                  onClick={promote}
                  disabled={busy || !preview.eligibility?.eligible}
                  className="px-4 py-2 text-[11px] font-display tracking-widest uppercase font-bold text-white bg-violet-600 hover:bg-violet-500 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "Promoting…" :
                    preview.eligibility?.eligible ? "Confirm Promotion" :
                    "Not Eligible"}
                </button>
                <button
                  onClick={() => setOpen(false)}
                  className="px-4 py-2 text-[11px] font-display tracking-widest uppercase text-white/70 border border-white/15 hover:bg-white/5"
                >Cancel</button>
                {preview.eligibility?.blockers?.length > 0 && (
                  <div className="text-[10px] text-amber-300 self-center">
                    Blockers: {preview.eligibility.blockers.join(", ")}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PreviewRow({ label, value, mono }) {
  return (
    <div className="flex gap-3 text-xs">
      <div className="text-white/45 tracking-widest uppercase font-display text-[9px] w-24">{label}</div>
      <div className={`text-white/85 ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}
function PreviewCell({ label, value, mono }) {
  return (
    <div className="border border-white/10 px-2 py-1.5">
      <div className="text-[9px] tracking-widest uppercase font-display text-white/40">{label}</div>
      <div className={`text-white/85 ${mono ? "font-mono" : ""}`}>
        {(value === null || value === undefined || value === "") ? <span className="text-white/30">—</span> : value}
      </div>
    </div>
  );
}

